<template>
  <div class="tw-w-full tw-min-w-72">
    <div class="tw-flex tw-flex-col tw-items-start tw-justify-center tw-w-full">
      <div class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-w-full">
        <div class="tw-py-4 tw-flex tw-flex-col tw-items-start tw-justify-center">
          <span class="tw-font-semibold md:tw-text-xl">
            {{ data.name }}
          </span>
          <span class="tw-text-sm tw-font-semibold tw-text-gray-600">
            {{ data.group }}
          </span>
          <div class="tw-grid tw-grid-cols-2 tw-gap-1 tw-text-xs">
            <div v-html="t('rc.label_last_time_changed')"></div>

            <div class="tw-text-xs tw-font-thin">
              {{ new Date(data.lastTimeChangedBy.lastTimeChanged * 1000).toLocaleString() }}
            </div>
            <div v-html="t('rc.label_changed_by')"></div>
            <div class="tw-text-xs tw-font-thin">
              {{ data.lastTimeChangedBy.username }}
            </div>
          </div>
        </div>
        <div
          v-if="
            warning &&
            (localVal > data.allowedMax || localVal < data.allowedMin) &&
            (data.allowedMin !== data.deviceMin || data.allowedMax !== data.deviceMax)
          "
          class="tw-text-red-500 tw-font-semibold tw-py-4 tw-text-xs md:tw-text-base">
          {{ t(warning) }}
        </div>
        <div
          v-else-if="showInfo && (data.allowedMin !== data.deviceMin || data.allowedMax !== data.deviceMax)"
          class="tw-p-4 tw-text-xs tw-relative">
          <button
            class="tw-absolute tw-top-[-0.5rem] tw-right-[-0.5rem] tw-rounded-full"
            @click="hideInfo">
            <Tooltip :text="t('rc.tooltip_message_hide_info')">&times;</Tooltip>
          </button>
          {{ t("rc.message_admin_range_limit") }}
          {{ data.allowedMin }}
          {{ data.unit }}
          -
          {{ data.allowedMax }}
          {{ data.unit }}
        </div>
      </div>

      <div
        v-if="!data.hasPendingChange && !blockInput"
        class="tw-relative tw-w-full tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-center">
        <div class="tw-flex tw-flex-row tw-items-center tw-justify-center">
          <div class="tw-py-8 tw-flex tw-flex-row tw-items-center">
            <span class="minMaxIndicator">
              {{ data.allowedMin }}
              {{ data.unit }}
            </span>
            <Slider
              v-model="localVal"
              @change="updateValue"
              :step="data.step"
              :min="data.allowedMin"
              :max="data.allowedMax"
              @end="handleWarning"
              class="md:tw-w-96 tw-w-48"
              :format="(val: number) => `${val.toFixed(data.decimals || 2).replace('.', ',')} ${data.unit}`" />
          </div>
          <span class="minMaxIndicator">
            {{ data.allowedMax }}
            {{ data.unit }}
          </span>
        </div>
        <div :class="['tw-h-[1px] sm:tw-h-16 tw-bg-gray-800 tw-w-2/3 sm:tw-w-[1px] tw-mx-2 tw-bg-opacity-30 ']"></div>
        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-relative">
          <span class="minMaxIndicator">
            {{ data.allowedMax }}
            {{ data.unit }}
          </span>
          <BaseInput
            :model-value="localVal"
            @input="(event: InputEvent) => updateValue(Number((event.target as HTMLInputElement).value))"
            class="tw-min-w-36"
            input-type="number"
            input-id="rc-input"
            :min="data.allowedMin"
            :max="data.allowedMax"
            :is-valid="warning ? false : undefined"
            :step="data.step"
            label="" />

          <span class="tw-absolute tw-text-xs tw-right-0 tw-bg-gray-300 tw-p-2">
            <span v-if="data.unit">
              {{ data.unit }}
            </span>
            <span
              v-else
              v-html="t('index.edit_widget_label_unit')"></span>
          </span>
          <span class="minMaxIndicator">
            {{ data.allowedMin }}
            {{ data.unit }}
          </span>
        </div>
      </div>
      <div v-else-if="!data.hasPendingChange && blockInput">
        <div
          class="tw-bg-status-red-200 tw-p-4"
          v-html="t('remote_control.cannot-set-without-limits')"></div>
        <div
          v-if="useUserConfigStore().canManageRemoteControl"
          class="tw-w-full tw-text-end">
          <router-link
            :to="{
              name: 'Manage Remote Control Permissions',
              params: {
                searchTearm: deviceName,
              },
              query: {
                searchTearm: deviceName,
              },
            }"
            @click="useRemoteControlStore().closeRCModal"
            class="tw-text-lg tw-font-semibold tw-italic tw-p-4">
            <span
              class="tw-underline"
              v-html="t('remote_control.admin-link-description')"></span>
          </router-link>
        </div>
      </div>
      <div
        v-else
        class="tw-animate-pulse">
        <span
          v-html="t('rc.label_pending_change')"
          class="tw-text-red-500 tw-font-semibold tw-py-4"></span>
        :
        <span class="tw-font-semibold tw-py-4">
          {{ data.currentSetPoint }}{{ data.unit }}
          -->
          {{ data.pendingValue }}{{ data.unit }}
        </span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { RCData } from "@/api/RemoteControlConnector";
import BaseInput from "@/components/library/BaseInput/BaseInput.vue";
import Tooltip from "@/components/library/Tooltip/Tooltip.vue";
import Slider from "@vueform/slider";
import { computed, ref, watch, type PropType } from "vue";
import { useTranslation } from "i18next-vue";
import { useRemoteControlStore } from "@/storeModules/remoteControl/remoteControlStore";
import { useUserConfigStore } from "@/storeModules/userConfigs";

const { t } = useTranslation();

const props = defineProps({
  data: {
    type: Object as PropType<RCData>,
    required: true,
  },
  value: {
    type: Number,
    required: false,
  },
  deviceName: {
    type: String,
  },
});

const localVal = ref(Number(props.value?.toFixed(props.data.decimals)) ?? 0);
const blockInput = computed(() => {
  const relevantKeys = ["allowedMin", "allowedMax", "adminDefinedMin", "adminDefinedMax"];
  const relevantValues = Object.values(props.data).filter((_value, index) => relevantKeys.includes(Object.keys(props.data)[index]));
  if (relevantValues.includes(null)) return true;
  return false;
});
const warning = ref("");

const showInfo = ref(true);
const emit = defineEmits(["update:value", "isValid"]);
const handleWarning = () => {
  if (localVal.value > props.data.allowedMax || localVal.value < props.data.allowedMin) {
    warning.value = "rc.warning_out_of_range";
    emit("isValid", false);
  } else {
    warning.value = "";
    emit("isValid", true);
  }
};

const hideInfo = () => {
  showInfo.value = false;
};

type RCHistoryValue = {
  name: string;
  value: number;
  index: number;
  subIndex: number;
  oldValue: number;
  unit: string;
};
const updateValue = (value: number) => {
  localVal.value = value;
  const output: RCHistoryValue = {
    value: Number(value.toFixed(props.data.decimals)),
    name: props.data.name,
    unit: props.data.unit,
    index: props.data.index,
    subIndex: props.data.subIndex,
    oldValue: props.data.currentSetPoint,
  };
  handleWarning();
  emit("update:value", output);
};

watch(
  () => props.value,
  () => {
    if (localVal.value !== props.value) {
      localVal.value = Number(props.value?.toFixed(props.data.decimals)) ?? 0;
    }
  }
);
</script>
<style src="@vueform/slider/themes/default.css"></style>
<style scoped lang="postcss">
.minMaxIndicator {
  @apply tw-text-xs tw-text-gray-500 tw-font-medium;
}
</style>
